import { useMemo, useState } from "react";
import type { Dataset } from "../lib/types";
import {
  generateCortexBrief,
  getCortexPrompt,
  type CortexBrief,
  type Finding,
  type Period,
  type Severity,
} from "../lib/cortexBrief";
import Card from "../components/Card";
import { showToast } from "../components/Toast";
import { exportCsv, printToPdf } from "../lib/export";

interface Props { data: Dataset }

const SEV_LABEL: Record<Severity, string> = {
  good: "GOOD",
  info: "INFO",
  watch: "WATCH",
  alert: "ALERT",
  critical: "CRITICAL",
};

function FindingCard({ f }: { f: Finding }) {
  return (
    <li className={`finding sev-${f.severity}`}>
      <div className="finding-head">
        <span className={`finding-sev ${f.severity}`}>{SEV_LABEL[f.severity]}</span>
        <h4 className="finding-title">{f.title}</h4>
        {f.numeric && <span className="finding-numeric">{f.numeric}</span>}
      </div>
      <p className="finding-body">{f.body}</p>
      <div className="finding-cites">
        {f.citations.map((c) => (
          <span key={c} className="finding-cite">{c}</span>
        ))}
      </div>
    </li>
  );
}

export default function Cortex({ data }: Props) {
  const [period, setPeriod] = useState<Period>("quarter");
  const [generating, setGenerating] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  // Stable brief per (data, period). Regenerate triggers a brief loading state.
  const [version, setVersion] = useState(0);
  const brief: CortexBrief = useMemo(() => generateCortexBrief(data, period),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, period, version]
  );
  const prompt = useMemo(() => getCortexPrompt(brief), [brief]);

  const regenerate = () => {
    setGenerating(true);
    setTimeout(() => {
      setVersion((v) => v + 1);
      setGenerating(false);
      showToast("Zen Insights brief regenerated", "success");
    }, 900);
  };

  const exportBrief = () => {
    const all = [...brief.hiddenGems, ...brief.watchlist, ...brief.forwardLook, ...brief.actions];
    exportCsv(
      all,
      [
        { header: "Section", value: (f) => f.kind },
        { header: "Severity", value: (f) => f.severity },
        { header: "Title", value: (f) => f.title },
        { header: "Body", value: (f) => f.body },
        { header: "Citations", value: (f) => f.citations.join("; ") },
        { header: "Numeric", value: (f) => f.numeric ?? "" },
      ],
      `zen-insights-brief-${brief.asOf}-${brief.period}`
    );
    showToast("Zen Insights brief exported (CSV)", "success");
  };

  return (
    <section className="tab-panel active">
      <div className="page-head">
        <div>
          <h1 className="page-title">
            Zen Insights <span className="page-pill">AI INSIGHT ENGINE</span>
          </h1>
          <p className="page-sub">
            Auto-synthesized findings across Portfolio Billing, AR &amp; Customer Risk, Net Income Forecast,
            and Customer 360. Every claim cites the KPI codes it derives from. Surfaces "hidden gems" —
            cross-cutting patterns easy to miss on any single card.
          </p>
        </div>
        <div className="page-controls">
          <div className="seg">
            {(["month", "quarter", "ytd"] as Period[]).map((p) => (
              <button key={p} className={`seg-btn${period === p ? " active" : ""}`} onClick={() => setPeriod(p)}>
                {p === "month" ? "Month" : p === "quarter" ? "Quarter" : "YTD"}
              </button>
            ))}
          </div>
          <button className="btn-ghost" onClick={exportBrief}>⬇ Export</button>
          <button className="btn-ghost" onClick={() => printToPdf()}>⬇ PDF</button>
          <button className="btn-primary" onClick={regenerate} disabled={generating}>
            {generating ? "✨ Generating…" : "✨ Regenerate"}
          </button>
        </div>
      </div>

      {/* Generation metadata strip */}
      <div className="cortex-meta">
        <div>
          <span className="cortex-meta-label">Generated</span>
          <span className="cortex-meta-val">{brief.generatedAt}</span>
        </div>
        <div>
          <span className="cortex-meta-label">Model</span>
          <span className="cortex-meta-val">cs-zen-insights · {brief.modelVintage}</span>
        </div>
        <div>
          <span className="cortex-meta-label">Snapshot as-of</span>
          <span className="cortex-meta-val">{brief.asOfDisplay}</span>
        </div>
        <div>
          <span className="cortex-meta-label">Scope</span>
          <span className="cortex-meta-val">
            {brief.scope.projects} projects · {brief.scope.customers} customers · {brief.scope.flagged} flagged · {brief.scope.criticalCustomers} critical
          </span>
        </div>
      </div>

      {/* Headline */}
      <div className={`cortex-headline${generating ? " gen" : ""}`}>
        <div className="cortex-headline-eyebrow">CFO BRIEF · HEADLINE</div>
        <div className="cortex-headline-text">{brief.headline}</div>
        <div className="cortex-headline-lead">{brief.paragraphLead}</div>
      </div>

      {/* Two-column: hidden gems + watchlist */}
      <div className="row-2">
        <Card span2 eyebrow="Cross-cutting findings" title="Hidden gems"
          info={{
            title: "Hidden gems",
            text:
              "Patterns the AI surfaces by joining cards across tabs. These are findings that don't show up on any single card — only when their signals are correlated. Each cites the KPI codes that produced it.",
          }}
        >
          {brief.hiddenGems.length > 0 ? (
            <ul className="finding-list">
              {brief.hiddenGems.map((f, i) => <FindingCard key={i} f={f} />)}
            </ul>
          ) : (
            <div className="cortex-empty">No hidden-gem findings this {period}. Portfolio is operating within tolerance.</div>
          )}
        </Card>

        <Card eyebrow="K-028 ranked" title="Customer watchlist"
          info={{
            title: "Customer watchlist",
            text: "Customers ranked by composite K-028 risk score. Each row breaks down the contributing signals: pay-cycle drift, Paydex baseline, external distress events.",
          }}
        >
          {brief.watchlist.length > 0 ? (
            <ul className="finding-list compact">
              {brief.watchlist.map((f, i) => <FindingCard key={i} f={f} />)}
            </ul>
          ) : (
            <div className="cortex-empty">No customers above the watchlist threshold.</div>
          )}
        </Card>
      </div>

      {/* Forward look + actions */}
      <div className="row-2">
        <Card eyebrow="30 / 60 / 90 day implications" title="Forward look"
          info={{
            title: "Forward look",
            text: "Probabilistic implications based on the current snapshot, the conformal forecast intervals (K-040), and the historical drift rates of K-013 / K-028.",
          }}
        >
          <ul className="finding-list compact">
            {brief.forwardLook.map((f, i) => <FindingCard key={i} f={f} />)}
          </ul>
        </Card>

        <Card span2 eyebrow="Decision-ready" title="Recommended actions this week"
          info={{
            title: "Recommended actions",
            text: "Actions prioritized by expected NPV impact + reversibility. Production swaps the rule-based logic here for the Layer-4 simulator — same shape, model-scored.",
          }}
        >
          {brief.actions.length > 0 ? (
            <ol className="finding-list ordered">
              {brief.actions.map((f, i) => <FindingCard key={i} f={f} />)}
            </ol>
          ) : (
            <div className="cortex-empty">No actions surfaced. Routine monitoring this week.</div>
          )}
        </Card>
      </div>

      {/* The prompt — collapsible */}
      <div className="cortex-prompt-wrap">
        <button className="cortex-prompt-toggle" onClick={() => setShowPrompt((v) => !v)} aria-expanded={showPrompt}>
          {showPrompt ? "▾ Hide prompt" : "▸ Show how this was generated"}
          <span className="cortex-prompt-toggle-tag">explainability · auditable</span>
        </button>
        {showPrompt && (
          <div className="cortex-prompt">
            <div className="cortex-prompt-section">
              <div className="cortex-prompt-label">SYSTEM</div>
              <pre className="cortex-prompt-body">{prompt.system}</pre>
            </div>
            <div className="cortex-prompt-section">
              <div className="cortex-prompt-label">USER</div>
              <pre className="cortex-prompt-body">{prompt.user}</pre>
            </div>
            <div className="cortex-prompt-foot">
              The brief is generated under explicit constraints: every claim cites a KPI code, refuses to invent data,
              uses exact deltas (no hedging adjectives). The output schema is rendered directly — there is no Markdown
              parsing layer between the model and the dashboard. Same prompt for the production engine; only the input
              snapshot changes.
            </div>
          </div>
        )}
      </div>

    </section>
  );
}
